import React, { useState } from "react";
import { Control, DeepMap, useFieldArray, UseFormMethods } from "react-hook-form";
import { FormDataFilter } from "../../schemas/DemoTypes";
import { Button, Divider, Dropdown, Form, Segment } from "semantic-ui-react";
import { Label } from "react-aria-components";
import { ListFieldDefinition } from "../../queries/getCrmListFieldDefinitions";
import { FilterForm } from "./FilterForm";
import { GetLookUpOptionsParamsType, LookUpOptionsType } from "../../queries/getCrmLookUpOptions";
import { ControlledInput } from "../../../components/ControlledForm";
import classNames from "classnames";
import { isErrorSelector, nestedErrorMessageSelector } from "./Utils";
import { getPicklistOptions } from "../../utils/getPicklistOptions";

const CloseIcon = ({ onClick }: { onClick: () => void }) => {
  return (
    <div
      tabIndex={0}
      role='button'
      className='tw-flex tw-h-[15px] tw-w-[15px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[8px] tw-text-[rgb(0,0,0,0.8)]'
      onClick={onClick}
    >
      <i className='fa-solid fa-x' />
    </div>
  );
};

const addNewFilter = (
  fieldDefinition: ListFieldDefinition & { name: string },
  appendFilter: (value: Partial<FormDataFilter> | Partial<FormDataFilter>[], shouldFocus?: boolean) => void,
) => {
  switch (fieldDefinition.type) {
    case "Text":
    case "TextArea":
    case "Address":
    case "Email":
    case "PhoneNumber":
    case "URL":
      appendFilter({
        type: fieldDefinition.type,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        filter: {
          jsonValue: [],
          stringCondition: "Equal",
        },
      });
      break;
    case "Number":
    case "Percent":
    case "Currency":
      appendFilter({
        type: fieldDefinition.type,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        filter: {
          numberCondition: "Equal",
          numberValue: {
            firstNumber: undefined,
            secondNumber: undefined,
          },
        },
      });
      break;
    case "Checkbox":
      appendFilter({
        type: fieldDefinition.type,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        filter: {
          booleanCondition: "True",
          booleanValue: true,
        },
      });
      break;
    case "Date":
    case "DateTime":
    case "Time":
      appendFilter({
        type: fieldDefinition.type,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        filter: {
          dateCondition: "Equal",
          dateValue: {
            dateOption: undefined,
            startDate: undefined,
            endDate: undefined,
          },
        },
      });
      break;
    case "Dropdown":
    case "DropdownMultiSelect":
      appendFilter({
        type: fieldDefinition.type,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        picklist: fieldDefinition.picklist ? getPicklistOptions(fieldDefinition.picklist) : [],
        filter: {
          jsonValue: [],
          dropdownCondition: "Any",
        },
      });
      break;
    case "LookUp":
      appendFilter({
        type: fieldDefinition.type,
        lookUpType: fieldDefinition.lookUpType,
        lookUpNeedsSearch: fieldDefinition.lookUpNeedsSearch,
        label: fieldDefinition.label,
        field: fieldDefinition.name,
        fieldId: fieldDefinition.fieldId,
        fieldTable: fieldDefinition.fieldTable,
        filter: {
          jsonValue: [],
          dropdownCondition: "Any",
        },
      });
      break;
    default:
      throw new Error(`Unsupported filter type`);
  }
};
interface IFilterGroupFormProps {
  control: Control;
  errors: DeepMap<any, any>;
  watch: UseFormMethods["watch"];
  setValue: UseFormMethods["setValue"];
  setError: UseFormMethods["setError"];
  register: UseFormMethods["register"];
  fieldDefinitions: Record<string, ListFieldDefinition>;
  lookUpOptionsFunction: (
    params: GetLookUpOptionsParamsType,
    enabled: boolean,
  ) => {
    isLoading: boolean;
    data:
      | {
          options: LookUpOptionsType[];
        }
      | undefined;
  };
}

export const FilterGroupForm = ({
  control,
  errors,
  watch,
  setValue,
  setError,
  register,
  fieldDefinitions,
  lookUpOptionsFunction,
}: IFilterGroupFormProps) => {
  const [displayAddNewFilterSegment, setDisplayAddNewFilterSegment] = useState(false);
  const [selectedFieldDefinition, setSelectedFieldDefinition] = useState<
    (ListFieldDefinition & { name: string }) | null
  >(null);
  const [editAdvancedLogic, setEditAdvancedLogic] = useState(false);
  const watchedAdvancedLogic = watch("advancedLogic");
  const [updatedAdvancedLogic, setUpdatedAdvancedLogic] = useState(watchedAdvancedLogic || "");
  const useAdvancedLogic = watch("useAdvancedLogic");
  register("useAdvancedLogic");
  const {
    fields: filters,
    remove: removeFilter,
    append: appendFilter,
  } = useFieldArray<FormDataFilter>({ control, name: "filters" });
  return (
    <Form className='tw-flex tw-flex-col tw-gap-4' noValidate>
      {" "}
      {/** noValidate hides default browser tooltip */}
      {filters.map((filter, index) => {
        const isError = !!nestedErrorMessageSelector(errors)(`filters.${index}.filter`);
        const isDate = filter.type === "Date" || filter.type === "DateTime" || filter.type === "Time";
        const isCalendarDate = (() => {
          if (filter.type === "Date" || filter.type === "DateTime" || filter.type === "Time") {
            if (filter.filter?.dateCondition === "Empty" || filter.filter?.dateCondition === "NotEmpty") {
              return false;
            }
            return !filter.filter?.dateValue?.dateOption;
          }
        })();
        return (
          <div className='tw-flex tw-items-center tw-justify-center tw-gap-2' key={filter.id}>
            {useAdvancedLogic && (
              // date filters have text at the bottom, so those need to align in the center, anything should just be aligned to the bottom
              // errors render text at the bottom, both types need specific margins
              <div
                className={classNames({
                  "tw-mt-auto": !isDate && !isError,
                  "tw-mb-4": !isDate && isError,
                  "tw-mt-px": isError && !isCalendarDate,
                  "tw-mb-12": isDate && isError,
                  "tw-mt-[2px]": isError && isCalendarDate,
                })}
              >
                {/** Outer div allows for circular shape */}
                <div>
                  <div className="tw-flex tw-h-[38px] tw-w-[38px] tw-items-center tw-justify-center tw-rounded-[50%] tw-bg-white tw-text-center tw-font-[Lato,_'Helvetica_Neue',_Arial,_Helvetica,_sans-serif] tw-text-[18px] tw-font-bold tw-shadow-semantic-ui-tag">
                    {index + 1}
                  </div>
                </div>
              </div>
            )}
            <Form.Field className='tw-m-0 tw-w-full' error={isError}>
              <FilterForm
                className='tw-m-0 tw-w-full'
                key={filter.id}
                control={control}
                errors={errors}
                filter={filter}
                index={index}
                setError={setError}
                lookUpOptionsFunction={lookUpOptionsFunction}
              />
            </Form.Field>
            <div
              className={classNames({
                "tw-mt-auto": !isDate && !isError,
                "tw-mb-4": !isDate && isError,
                "tw-mt-px": isError && !isCalendarDate,
                "tw-mb-12": isDate && isError,
                "tw-mt-[2px]": isError && isCalendarDate,
              })}
            >
              <div className='tw-flex tw-h-[38px] tw-items-center tw-justify-center'>
                <CloseIcon
                  onClick={() => {
                    removeFilter(index);
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
      {displayAddNewFilterSegment && (
        <Segment className='tw-m-0 tw-p-4'>
          <Label className='tw-font-bold'>Add Filter</Label>
          <div className='tw-flex tw-h-fit tw-items-center tw-justify-center tw-gap-2'>
            <Dropdown
              className='tw-h-fit tw-w-full'
              fluid
              search
              selection
              options={Object.entries(fieldDefinitions).map(([name, field]) => {
                return {
                  key: name,
                  value: name,
                  text: field.label,
                };
              })}
              onChange={(e, data) => {
                if (typeof data.value !== "string") return;
                const selectedField = fieldDefinitions[data.value];
                if (selectedField) {
                  setSelectedFieldDefinition({
                    ...selectedField,
                    name: data.value,
                  });
                }
              }}
            />
            <Button
              className='tw-h-fit tw-w-fit tw-min-w-[90px]'
              icon='plus'
              content='Add'
              secondary
              disabled={!selectedFieldDefinition}
              onClick={() => {
                if (!selectedFieldDefinition) return;
                addNewFilter(selectedFieldDefinition, appendFilter);
                setSelectedFieldDefinition(null);
                setDisplayAddNewFilterSegment(false);
              }}
            />
            <CloseIcon
              onClick={() => {
                setDisplayAddNewFilterSegment(false);
                setSelectedFieldDefinition(null);
              }}
            />
          </div>
        </Segment>
      )}
      {!displayAddNewFilterSegment && (
        <Button
          className='tw-mr-auto'
          icon='plus'
          content='Add Filter'
          secondary
          onClick={() => {
            setDisplayAddNewFilterSegment(true);
          }}
        />
      )}
      <Divider className='tw-m-0' />
      {!useAdvancedLogic && (
        <Button
          className='tertiary tw-mr-auto'
          content='Use Filter Logic'
          onClick={() => {
            setValue("useAdvancedLogic", true);
          }}
        />
      )}
      {useAdvancedLogic && (
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <Label className='tw-text-lg tw-font-bold'>Advanced Logic</Label>
          <div className='tw-flex tw-items-center tw-justify-center'>
            <div className='tw-flex tw-items-center tw-justify-center'>
              <Form.Field error={editAdvancedLogic && isErrorSelector(errors)("advancedLogic")}>
                <ControlledInput
                  name='advancedLogic'
                  readonly={!editAdvancedLogic}
                  required
                  control={control}
                  errors={editAdvancedLogic ? errors : {}} // only show errors when editing, prevents error message from rendering next to readonly input
                  className='tw-w-fit' //prevents the input from stretching to full width when error is present
                />
              </Form.Field>
            </div>
            {!editAdvancedLogic && (
              <Button
                secondary
                className='tw-ml-auto'
                content='Edit'
                onClick={() => {
                  setEditAdvancedLogic(true);
                }}
              />
            )}
            {editAdvancedLogic && (
              <Button
                className='tw-mb-auto tw-ml-auto'
                primary
                content='Apply'
                onClick={async () => {
                  //validate just the advanced logic field
                  const isUpdatedAdvancedLogicValid = await control.trigger("advancedLogic");
                  if (isUpdatedAdvancedLogicValid) {
                    setEditAdvancedLogic(false);
                    //save the new value
                    setUpdatedAdvancedLogic(watchedAdvancedLogic);
                    return;
                  }
                }}
              />
            )}
            {/**class allows close icon to stay aligned with the button and input when an error is present */}
            <div className='tw-mb-auto tw-ml-2 tw-flex tw-h-[38px] tw-items-center tw-justify-center'>
              <CloseIcon
                onClick={() => {
                  setValue("useAdvancedLogic", false);
                  setEditAdvancedLogic(false);
                  // reset form value back to last saved value
                  setValue("advancedLogic", updatedAdvancedLogic);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Form>
  );
};
